import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface ThemeContextType {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

type Theme = "light" | "dark";

function readInitialTheme(): Theme {
  if (typeof document !== "undefined") {
    const bodyTheme = document.body.getAttribute("data-theme");
    if (bodyTheme === "light" || bodyTheme === "dark") return bodyTheme;
  }
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem("app-theme");
    if (stored === "light" || stored === "dark") return stored;
  }
  return "light";
}

function applyThemeToDocument(next: Theme) {
  if (typeof document === "undefined") return;
  if (document.body.getAttribute("data-theme") !== next) {
    document.body.setAttribute("data-theme", next);
  }
  document.documentElement.style.colorScheme = next;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const maybeApply = (next: unknown) => {
      if (next !== "light" && next !== "dark") return;
      if (next === themeRef.current) return;
      setTheme(next);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === "app-theme") maybeApply(e.newValue);
    };

    const onThemeChange = (e: Event) => {
      const custom = e as CustomEvent<Theme>;
      maybeApply(custom.detail);
    };

    const onBodyAttrChange = () => {
      maybeApply(document.body.getAttribute("data-theme"));
    };

    const observer = new MutationObserver(onBodyAttrChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    window.addEventListener("storage", onStorage);
    window.addEventListener("themechange", onThemeChange);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("themechange", onThemeChange);
    };
  }, []);

  const toggleTheme = () => {
    const next: Theme = themeRef.current === "light" ? "dark" : "light";
    setTheme(next);
    applyThemeToDocument(next);
    try {
      window.localStorage.setItem("app-theme", next);
    } catch {
      /* ignore storage failures */
    }
    window.dispatchEvent(new CustomEvent("themechange", { detail: next }));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
